import { useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, Clock, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Premium reschedule modal.
 * Saves via the existing PATCH /appointments/{id} endpoint by sending
 * { start_time (ISO), duration_min } — backend auto-syncs `date`.
 */
export default function RescheduleModal({ appointment, onClose, onDone }) {
  const original = new Date(appointment.start_time);
  const pad = (n) => String(n).padStart(2, "0");
  const isoLocal = `${original.getFullYear()}-${pad(original.getMonth() + 1)}-${pad(original.getDate())}T${pad(original.getHours())}:${pad(original.getMinutes())}`;

  const [date, setDate] = useState(isoLocal.slice(0, 10));
  const [time, setTime] = useState(isoLocal.slice(11, 16));
  const [duration, setDuration] = useState(appointment.duration_min || 30);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time || !duration) return;
    setSaving(true);
    try {
      const start = new Date(`${date}T${time}:00`);
      if (isNaN(start.getTime())) throw new Error("Invalid date/time");
      const { data } = await api.patch(`/appointments/${appointment.appointment_id}`, {
        start_time: start.toISOString(),
        duration_min: Number(duration),
      });
      const notif = data?.notification;
      let desc;
      if (notif?.success) desc = `WhatsApp sent to ${appointment.customer_name || "customer"}.`;
      else if (notif && notif.error && /phone/i.test(notif.error)) desc = "No customer phone on file — WhatsApp skipped.";
      else if (notif && notif.error) desc = `WhatsApp not sent: ${notif.error}`;
      toast.success("Appointment rescheduled.", { description: desc });
      onDone?.(date); // pass new date so parent can jump the calendar there
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not reschedule");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-nexa-ink/40 backdrop-blur-[2px] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="apt-reschedule-modal"
    >
      <motion.form
        initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="lux-card w-full max-w-lg p-6 relative max-h-[85vh] overflow-y-auto"
      >
        <button
          type="button" onClick={onClose}
          className="absolute top-4 right-4 text-nexa-mute hover:text-nexa-ink transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" strokeWidth={1.7} />
        </button>

        <div className="lux-eyebrow">Reschedule</div>
        <h2 className="text-[17px] font-semibold tracking-tight text-nexa-ink mt-1 mb-1">
          {appointment.customer_name}
        </h2>
        <p className="text-[13px] text-nexa-mute mb-5">{appointment.service}</p>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="lux-label flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-nexa-mute" strokeWidth={1.5}/> Date</label>
              <input
                required type="date" className="lux-input"
                value={date} onChange={(e) => setDate(e.target.value)}
                data-testid="apt-reschedule-date"
              />
            </div>
            <div>
              <label className="lux-label flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-nexa-mute" strokeWidth={1.5}/> Time</label>
              <input
                required type="time" className="lux-input"
                value={time} onChange={(e) => setTime(e.target.value)}
                data-testid="apt-reschedule-time"
              />
            </div>
          </div>
          <div>
            <label className="lux-label">Duration (minutes)</label>
            <input
              required type="number" min={5} step={5} className="lux-input"
              value={duration} onChange={(e) => setDuration(e.target.value)}
              data-testid="apt-reschedule-duration"
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="lux-btn-secondary">Cancel</button>
          <button
            type="submit" disabled={saving}
            className="lux-btn-accent"
            data-testid="apt-reschedule-submit"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}
